import React, { memo, useState } from 'react';
import {
  Mail, Phone, MapPin, Edit, Trash2, CreditCard, MessageSquare, Clock, Check, X, ChevronLeft, ChevronRight, AlertTriangle, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/api';
import { StatusBadge } from '../StatusBadge';
import { getStatusColor, formatStatusLabel } from '../../utils/statusColors';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Columns3, Flame } from 'lucide-react';

const API_URL = getApiUrl();

// Column chooser — persisted per browser. Trial Status is a manual flag (often stale),
// so it is hidden by default; everything else stays visible.
export const LEAD_COLUMNS = [
  { key: 'lead', label: 'Lead', locked: true },
  { key: 'contact', label: 'Contact' },
  { key: 'country', label: 'Country' },
  { key: 'created', label: 'Created' },
  { key: 'lead_status', label: 'Lead Status' },
  { key: 'trial_status', label: 'Trial Status' },
  { key: 'quality', label: 'Quality' },
  { key: 'enrollment', label: 'Enrollment' },
  { key: 'sales_notes', label: 'Sales Notes' },
  { key: 'pregnancy_notes', label: 'Pregnancy Notes' },
  { key: 'actions', label: 'Actions', locked: true },
];
const COLS_KEY = 'ops_leads_columns_v2';
const defaultVisible = () => LEAD_COLUMNS.reduce((a, c) => ({ ...a, [c.key]: !c.defaultHidden }), {});
const loadVisible = () => {
  try { return { ...defaultVisible(), ...(JSON.parse(localStorage.getItem(COLS_KEY) || '{}')) }; } catch (e) { return defaultVisible(); }
};

const ColumnChooser = ({ show, setShow }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" className="btn-secondary text-xs py-1.5 inline-flex items-center gap-1.5" data-testid="leads-column-chooser-btn">
        <Columns3 className="w-3.5 h-3.5" /> Columns
      </button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-56 p-2 bg-popover" data-testid="leads-column-chooser">
      {LEAD_COLUMNS.map((c) => (
        <label key={c.key} className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm ${c.locked ? 'text-slate-400' : 'hover:bg-muted cursor-pointer'}`}>
          <input type="checkbox" className="rounded" checked={!!show[c.key]} disabled={c.locked}
            onChange={(e) => setShow({ ...show, [c.key]: e.target.checked })} data-testid={`leads-col-toggle-${c.key}`} />
          {c.label}
        </label>
      ))}
      <button type="button" className="mt-1 w-full text-xs text-primary hover:underline py-1" onClick={() => setShow(defaultVisible())} data-testid="leads-col-reset">Reset to default</button>
    </PopoverContent>
  </Popover>
);

// Labels are local; colours come from the global status map.
const LEAD_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'dnp', label: 'DNP' },
  { value: 'callback_later', label: 'Callback Later' },
  { value: 'future_prospect', label: 'Future Prospect' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'number_not_in_service', label: 'Number Not in Service' },
  { value: 'wip', label: 'WIP' },
  { value: 'not_eligible', label: 'Not Eligible' },
];

// 'unpaid' is retired — payment state lives in trial_payment_status; legacy rows read as Pending.
const trialStatusOf = (lead) => (!lead.trial_status || lead.trial_status === 'unpaid') ? 'pending' : lead.trial_status;

const TRIAL_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'no_show', label: 'No Show' },
  { value: 'cancelled', label: 'Cancelled' },
];

const formatStatus = (status) => formatStatusLabel(status);

const LeadTable = memo(({
  leads, loading, total, page, totalPages, goToPage,
  user, onEdit, onDelete, onPayment,
  onRefresh, updateLeadLocal,
  canMerge, selectedForMerge, toggleMergeSelect,
  bulkMode, bulkSelected, toggleBulkSelect, toggleBulkSelectPage,
}) => {
  const [editingSalesNotes, setEditingSalesNotes] = useState(null);
  const [salesNotesValue, setSalesNotesValue] = useState('');
  const [editingPregnancyNotes, setEditingPregnancyNotes] = useState(null);
  const [pregnancyNotesValue, setPregnancyNotesValue] = useState('');
  const [editingCallback, setEditingCallback] = useState(null);
  const [callbackDateTime, setCallbackDateTime] = useState('');
  const [show, setShowState] = useState(loadVisible);
  const setShow = (next) => { setShowState(next); try { localStorage.setItem(COLS_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ } };
  const visibleCount = LEAD_COLUMNS.filter((c) => show[c.key]).length + (canMerge || bulkMode ? 1 : 0);
  const pageIds = leads.map((l) => l.lead_id);
  const pageAllSelected = bulkMode && pageIds.length > 0 && pageIds.every((id) => bulkSelected?.includes(id));
  const isPicked = (id) => (bulkMode ? bulkSelected?.includes(id) : selectedForMerge?.includes(id)) || false;

  const canEdit = ['super_admin', 'pre_sales', 'sales', 'operations'].includes(user?.role);
  const canViewOnly = user?.role === 'marketing';
  const canDelete = user?.role === 'super_admin';
  const canCreatePaymentLink = ['super_admin', 'sales', 'pre_sales'].includes(user?.role);

  const handleInlineUpdate = async (leadId, field, value) => {
    if (field === 'lead_status' && value === 'callback_later') {
      setEditingCallback(leadId);
      setCallbackDateTime('');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/leads/${leadId}`, { [field]: value });
      updateLeadLocal(leadId, { [field]: value });
      toast.success('Updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleCallbackSubmit = async () => {
    if (!editingCallback || !callbackDateTime) { toast.error('Please select a callback date and time'); return; }
    try {
      await axios.put(`${API_URL}/api/leads/${editingCallback}`, { lead_status: 'callback_later', callback_datetime: callbackDateTime });
      updateLeadLocal(editingCallback, { lead_status: 'callback_later', callback_datetime: callbackDateTime });
      setEditingCallback(null);
      toast.success('Callback scheduled successfully');
    } catch (error) { toast.error('Failed to schedule callback'); }
  };

  const handleSalesNotesUpdate = async () => {
    if (!editingSalesNotes) return;
    try {
      await axios.put(`${API_URL}/api/leads/${editingSalesNotes}`, { sales_notes: salesNotesValue });
      updateLeadLocal(editingSalesNotes, { sales_notes: salesNotesValue });
      setEditingSalesNotes(null);
      toast.success('Sales notes updated');
    } catch (error) { toast.error('Failed to update sales notes'); }
  };

  const handlePregnancyNotesUpdate = async () => {
    if (!editingPregnancyNotes) return;
    try {
      await axios.put(`${API_URL}/api/leads/${editingPregnancyNotes}`, { pregnancy_notes: pregnancyNotesValue });
      updateLeadLocal(editingPregnancyNotes, { pregnancy_notes: pregnancyNotesValue });
      setEditingPregnancyNotes(null);
      toast.success('Pregnancy notes updated');
    } catch (error) { toast.error('Failed to update pregnancy notes'); }
  };

  return (
    <>
      {/* Desktop table (md+) */}
      <div className="card overflow-hidden hidden md:block">
        <div className="flex items-center justify-between px-4 py-2 border-b border-border/70 bg-muted/40">
          <p className="text-xs text-muted-foreground" data-testid="leads-table-count">{total} lead{total === 1 ? '' : 's'}</p>
          <ColumnChooser show={show} setShow={setShow} />
        </div>
        <div className="overflow-x-auto" style={{ maxWidth: '100%' }}>
          <table className="w-full" style={{ minWidth: visibleCount > 8 ? '1400px' : '900px' }}>
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {(canMerge || bulkMode) && (
                  <th className="p-4 w-10">
                    {bulkMode ? (
                      <input type="checkbox" checked={pageAllSelected}
                        onChange={() => toggleBulkSelectPage?.(pageIds, !pageAllSelected)}
                        className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                        title="Select every lead on this page" data-testid="bulk-select-page" />
                    ) : <span className="sr-only">Select</span>}
                  </th>
                )}
                <th className={`p-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider ${show.lead ? "" : "hidden"}`}>Lead</th>
                <th className={`p-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider ${show.contact ? "" : "hidden"}`}>Contact</th>
                <th className={`p-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider ${show.country ? "" : "hidden"}`}>Country</th>
                <th className={`p-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider ${show.created ? "" : "hidden"}`}>Created</th>
                <th className={`p-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider ${show.lead_status ? "" : "hidden"}`} style={{ minWidth: '140px' }}>Lead Status</th>
                <th className={`p-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider ${show.trial_status ? "" : "hidden"}`} style={{ minWidth: '130px' }}>Trial Status</th>
                <th className={`p-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider ${show.quality ? "" : "hidden"}`}>Quality</th>
                <th className={`p-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider ${show.enrollment ? "" : "hidden"}`}>Enrollment</th>
                <th className={`p-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider ${show.sales_notes ? "" : "hidden"}`} style={{ minWidth: '180px' }}>Sales Notes</th>
                <th className={`p-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider ${show.pregnancy_notes ? "" : "hidden"}`} style={{ minWidth: '180px' }}>Pregnancy Notes</th>
                <th className={`p-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider ${show.actions ? "" : "hidden"}`} style={{ minWidth: '140px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={visibleCount} className="p-8 text-center text-slate-500"><div className="flex justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div></div></td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={visibleCount} className="p-8 text-center text-slate-500">No leads found</td></tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.lead_id}
                    onClick={(e) => {
                      if (bulkMode || e.target.closest('button,a,input,select,textarea,label,[data-no-row-open]')) return;
                      onEdit(lead);
                    }}
                    className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${isPicked(lead.lead_id) ? (bulkMode ? 'bg-rose-50' : 'bg-amber-50') : ''}`} data-testid={`lead-row-${lead.lead_id}`}>
                    {/* Merge / bulk-select checkbox */}
                    {(canMerge || bulkMode) && (
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={isPicked(lead.lead_id)}
                          onChange={() => (bulkMode ? toggleBulkSelect?.(lead.lead_id) : toggleMergeSelect?.(lead.lead_id))}
                          className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                          data-testid={bulkMode ? `bulk-checkbox-${lead.lead_id}` : `merge-checkbox-${lead.lead_id}`}
                        />
                      </td>
                    )}
                    {/* Lead Name */}
                    <td className={`p-4 ${show.lead ? "" : "hidden"}`}>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => { if (!bulkMode) onEdit(lead); }} className="font-medium text-slate-900 hover:text-indigo-600 hover:underline text-left" data-testid={`open-lead-${lead.lead_id}`}>{lead.full_name}</button>
                        {lead.is_hot && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 rounded-full" title={(lead.lead_score_reasons || []).join(' · ') || 'HOT lead (LMS score)'} data-testid={`hot-badge-${lead.lead_id}`}>
                            <Flame className="w-3 h-3" /> HOT{typeof lead.lead_score === 'number' ? ` ${lead.lead_score}` : ''}
                          </span>
                        )}
                        {lead.lms_tier && lead.lms_tier !== 'free' && (
                          <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${lead.lms_tier === 'elite' ? 'bg-emerald-100 text-emerald-700' : lead.lms_tier === 'soulai_paid' ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`tier-badge-${lead.lead_id}`}>
                            {lead.lms_tier.replace('_', ' ')}
                          </span>
                        )}
                        {(lead.open_request_count || 0) > 0 && (
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-sky-100 text-sky-700 rounded-full" data-testid={`request-badge-${lead.lead_id}`}>
                            {lead.open_request_count} request{lead.open_request_count > 1 ? 's' : ''}
                          </span>
                        )}
                        {lead.consultation_requested && (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-teal-100 text-teal-700 rounded-full" data-testid={`consultation-badge-${lead.lead_id}`}>
                            Consultation
                          </span>
                        )}
                        {lead.re_registration_count > 0 && (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full" title={`Re-registered ${lead.re_registration_count} time(s)`}>
                            Re-reg #{lead.re_registration_count}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{lead.funnel_type || 'Direct'}</div>
                      {lead.consultation_requested && lead.concern && (
                        <div className="text-xs text-teal-600 mt-0.5 truncate max-w-[200px]" title={lead.concern}>Concern: {lead.concern}</div>
                      )}
                      {lead.consultation_booking_date && (
                        <div className="text-xs text-indigo-600 mt-0.5 font-medium" data-testid={`consultation-date-${lead.lead_id}`}>
                          {new Date(lead.consultation_booking_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          {' '}
                          {(() => { const [h,m] = (lead.consultation_booking_time || '').split(':'); const d = new Date(); d.setHours(parseInt(h)||0, parseInt(m)||0); return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }); })()}
                          {' IST'}
                          {lead.consultation_user_timezone && lead.consultation_user_timezone !== (lead.consultation_booking_timezone || 'Asia/Kolkata') && (
                            <span className="text-slate-400 font-normal"> / {lead.consultation_user_timezone.split('/').pop()}</span>
                          )}
                        </div>
                      )}
                    </td>
                    {/* Contact */}
                    <td className={`p-4 ${show.contact ? "" : "hidden"}`}>
                      <div className="flex items-center gap-1 text-slate-600"><Mail className="w-3 h-3" /><span className="text-xs">{lead.email}</span></div>
                      <div className="flex items-center gap-1 text-slate-600 mt-1"><Phone className="w-3 h-3" /><span className="text-xs">{lead.phone}</span>
                        {(lead.alternate_phones || []).length > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500" title={lead.alternate_phones.join(', ')} data-testid={`alt-phone-count-${lead.lead_id}`}>+{lead.alternate_phones.length}</span>
                        )}
                      </div>
                    </td>
                    {/* Country */}
                    <td className={`p-4 ${show.country ? "" : "hidden"}`}>
                      <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" /><span>{lead.country}</span>
                        {lead.timezone && <span className="text-xs text-slate-500 ml-1">({lead.timezone})</span>}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${lead.domestic_or_international === 'domestic' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}`}>
                        {lead.domestic_or_international}
                      </span>
                      {lead.intl_signal_conflict && !lead.country_override && (
                        <span className="ml-1 inline-flex items-center text-amber-600" title="Device signals disagree with resolved country — open lead to review" data-testid={`intl-conflict-${lead.lead_id}`}><AlertTriangle className="w-3.5 h-3.5" /></span>
                      )}
                      {lead.country_override && (
                        <span className="ml-1 text-[10px] text-sky-700" title={`Manual override by ops (${lead.country_override.mode})`} data-testid={`country-override-${lead.lead_id}`}>override</span>
                      )}
                    </td>
                    {/* Created */}
                    <td className={`p-4 ${show.created ? "" : "hidden"}`}>
                      <div className="text-sm text-slate-600">{lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</div>
                      <div className="text-xs text-slate-400">{lead.created_at ? new Date(lead.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
                      {lead.original_created_at && lead.re_registration_count > 0 && (
                        <div className="text-xs text-purple-500 mt-1">First: {new Date(lead.original_created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                      )}
                    </td>
                    {/* Lead Status Dropdown */}
                    <td className={`p-4 ${show.lead_status ? "" : "hidden"}`}>
                      <div className="flex flex-col gap-1">
                        {canEdit ? (
                          <select
                            value={lead.lead_status || 'new'}
                            onChange={(e) => handleInlineUpdate(lead.lead_id, 'lead_status', e.target.value)}
                            className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${getStatusColor(lead.lead_status || 'new')}`}
                            data-testid={`lead-status-${lead.lead_id}`}
                          >
                            {LEAD_STATUS_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                          </select>
                        ) : (
                          <StatusBadge status={lead.lead_status || 'new'}>
                            {LEAD_STATUS_OPTIONS.find(o => o.value === (lead.lead_status || 'new'))?.label || 'New'}
                          </StatusBadge>
                        )}
                        {lead.lead_status === 'callback_later' && lead.callback_datetime && (
                          <div
                            className="flex items-center gap-1 text-xs text-amber-600 cursor-pointer hover:text-amber-700"
                            onClick={() => { setEditingCallback(lead.lead_id); setCallbackDateTime(lead.callback_datetime); }}
                          >
                            <Clock className="w-3 h-3" />
                            <span>{new Date(lead.callback_datetime).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    {/* Trial Status */}
                    <td className={`p-4 ${show.trial_status ? "" : "hidden"}`}>
                      {canEdit ? (
                        <select
                          value={trialStatusOf(lead)}
                          onChange={(e) => handleInlineUpdate(lead.lead_id, 'trial_status', e.target.value)}
                          className={`text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer ${getStatusColor(trialStatusOf(lead))}`}
                          data-testid={`trial-status-${lead.lead_id}`}
                        >
                          {TRIAL_STATUS_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                        </select>
                      ) : (
                        <StatusBadge status={trialStatusOf(lead)} />
                      )}
                    </td>
                    {/* Quality */}
                    <td className={`p-4 ${show.quality ? "" : "hidden"}`}>
                      {lead.lead_quality && lead.lead_quality !== 'not_rated'
                        ? <StatusBadge status={lead.lead_quality} />
                        : <span className="text-slate-400 text-xs">—</span>}
                    </td>
                    {/* Enrollment */}
                    <td className={`p-4 ${show.enrollment ? "" : "hidden"}`}>
                      <StatusBadge status={lead.enrollment_status || 'not_enrolled'}>
                        {formatStatus(lead.enrollment_status) || 'Not Enrolled'}
                      </StatusBadge>
                    </td>
                    {/* Sales Notes */}
                    <td className={`p-4 ${show.sales_notes ? "" : "hidden"}`}>
                      <div className={`flex items-center gap-2 group ${canEdit ? 'cursor-pointer' : ''}`} onClick={() => { if (canEdit) { setEditingSalesNotes(lead.lead_id); setSalesNotesValue(lead.sales_notes || ''); } }}>
                        <div className="text-xs text-slate-600 max-w-[160px] truncate">
                          {lead.sales_notes || <span className="text-slate-400 italic">Add notes...</span>}
                        </div>
                        {canEdit && <MessageSquare className="w-3 h-3 text-slate-400 group-hover:text-indigo-600" />}
                      </div>
                    </td>
                    {/* Pregnancy Notes */}
                    <td className={`p-4 ${show.pregnancy_notes ? "" : "hidden"}`}>
                      <div className={`flex items-center gap-2 group ${canEdit ? 'cursor-pointer' : ''}`} onClick={() => { if (canEdit) { setEditingPregnancyNotes(lead.lead_id); setPregnancyNotesValue(lead.pregnancy_notes || ''); } }}>
                        <div className="text-xs text-slate-600 max-w-[160px] truncate">
                          {lead.pregnancy_notes || <span className="text-slate-400 italic">Add notes...</span>}
                        </div>
                        {canEdit && <MessageSquare className="w-3 h-3 text-slate-400 group-hover:text-pink-600" />}
                      </div>
                    </td>
                    {/* Actions */}
                    <td className={`p-4 ${show.actions ? "" : "hidden"}`}>
                      <div className="flex items-center gap-2">
                        {canCreatePaymentLink && (
                          <button onClick={() => onPayment(lead)} className={`p-2 rounded-md transition-colors ${lead.enrollment_status === 'enrolled' ? 'text-emerald-600 hover:bg-emerald-50' : 'text-indigo-600 hover:bg-indigo-50'}`} title={lead.enrollment_status === 'enrolled' ? 'Generate Renewal Payment Link' : 'Create Payment Link'} data-testid={`create-payment-${lead.lead_id}`}>
                            <CreditCard className="w-4 h-4" />
                          </button>
                        )}
                        {canEdit && (
                          <button onClick={() => onEdit(lead)} className="p-2 text-slate-600 hover:bg-slate-100 rounded-md transition-colors" title="Edit" data-testid={`edit-lead-${lead.lead_id}`}>
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        {canViewOnly && (
                          <button onClick={() => onEdit(lead)} className="p-2 text-slate-600 hover:bg-slate-100 rounded-md transition-colors" title="View" data-testid={`view-lead-${lead.lead_id}`}>
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => onDelete(lead.lead_id)} className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Delete" data-testid={`delete-lead-${lead.lead_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50" data-testid="pagination">
            <div className="text-sm text-slate-600">
              Showing {((page - 1) * 50) + 1} - {Math.min(page * 50, total)} of {total} leads
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className="p-2 rounded-md border border-slate-300 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) { pageNum = i + 1; }
                else if (page <= 3) { pageNum = i + 1; }
                else if (page >= totalPages - 2) { pageNum = totalPages - 4 + i; }
                else { pageNum = page - 2 + i; }
                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={`px-3 py-1 rounded-md text-sm font-medium ${page === pageNum ? 'bg-indigo-600 text-white' : 'border border-slate-300 hover:bg-slate-100 text-slate-700'}`}
                    data-testid={`page-${pageNum}-btn`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                className="p-2 rounded-md border border-slate-300 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile card list (< md) */}
      <div className="md:hidden space-y-3" data-testid="leads-mobile-list">
        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-slate-500">
            <div className="flex justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div></div>
          </div>
        ) : leads.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-slate-500">No leads found</div>
        ) : (
          leads.map((lead) => (
            <div
              key={lead.lead_id}
              className={`bg-white rounded-xl border p-4 shadow-sm ${selectedForMerge?.includes(lead.lead_id) ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}
              data-testid={`lead-card-${lead.lead_id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {canMerge && (
                      <input
                        type="checkbox"
                        checked={selectedForMerge?.includes(lead.lead_id) || false}
                        onChange={() => toggleMergeSelect?.(lead.lead_id)}
                        className="w-4 h-4 text-indigo-600 rounded border-slate-300"
                        data-testid={`merge-checkbox-mobile-${lead.lead_id}`}
                      />
                    )}
                    <span className="font-semibold text-slate-900 truncate">{lead.full_name}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{lead.funnel_type || 'Direct'} · {lead.country}</div>
                </div>
                <StatusBadge status={lead.lead_status || 'new'} size="xs">
                  {LEAD_STATUS_OPTIONS.find(o => o.value === (lead.lead_status || 'new'))?.label || 'New'}
                </StatusBadge>
              </div>

              <div className="space-y-1.5 text-xs">
                <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 text-indigo-600 font-medium">
                  <Phone className="w-3.5 h-3.5" /> {lead.phone}
                </a>
                <div className="flex items-center gap-1.5 text-slate-600 truncate">
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{lead.email}</span>
                </div>
                {lead.callback_datetime && lead.lead_status === 'callback_later' && (
                  <div className="flex items-center gap-1.5 text-amber-600">
                    <Clock className="w-3.5 h-3.5" /> {new Date(lead.callback_datetime).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1.5 flex-wrap">
                <StatusBadge status={trialStatusOf(lead)} size="xs">Trial: {formatStatusLabel(trialStatusOf(lead))}</StatusBadge>
                <StatusBadge status={lead.enrollment_status || 'not_enrolled'} size="xs">
                  {lead.enrollment_status === 'enrolled' ? 'Enrolled' : 'Not Enrolled'}
                </StatusBadge>
                {lead.lead_quality && lead.lead_quality !== 'not_rated' && (
                  <StatusBadge status={lead.lead_quality} size="xs">Quality: {formatStatusLabel(lead.lead_quality)}</StatusBadge>
                )}
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2">
                {canCreatePaymentLink && (
                  <button
                    onClick={() => onPayment(lead)}
                    className={`flex items-center justify-center gap-1 text-xs font-medium py-2 rounded-lg ${lead.enrollment_status === 'enrolled' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-indigo-600 text-white'}`}
                    data-testid={`create-payment-mobile-${lead.lead_id}`}
                  >
                    <CreditCard className="w-3.5 h-3.5" /> Pay
                  </button>
                )}
                {canEdit && (
                  <button
                    onClick={() => onEdit(lead)}
                    className="flex items-center justify-center gap-1 text-xs font-medium py-2 rounded-lg bg-slate-100 text-slate-700"
                    data-testid={`edit-lead-mobile-${lead.lead_id}`}
                  >
                    <Edit className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
                {canViewOnly && (
                  <button
                    onClick={() => onEdit(lead)}
                    className="flex items-center justify-center gap-1 text-xs font-medium py-2 rounded-lg bg-slate-100 text-slate-700"
                    data-testid={`view-lead-mobile-${lead.lead_id}`}
                  >
                    <Eye className="w-3.5 h-3.5" /> View
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => onDelete(lead.lead_id)}
                    className="flex items-center justify-center gap-1 text-xs font-medium py-2 rounded-lg bg-red-50 text-red-600 border border-red-100"
                    data-testid={`delete-lead-mobile-${lead.lead_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Del
                  </button>
                )}
              </div>
            </div>
          ))
        )}

        {/* Mobile pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-3 py-2 mt-2" data-testid="pagination-mobile">
            <button onClick={() => goToPage(page - 1)} disabled={page <= 1} className="flex items-center gap-1 text-xs font-medium text-slate-700 disabled:opacity-40">
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-xs text-slate-600">Page {page} of {totalPages}</span>
            <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages} className="flex items-center gap-1 text-xs font-medium text-slate-700 disabled:opacity-40">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Sales Notes Editor Modal */}
      {editingSalesNotes && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Edit Sales Notes</h3>
              <button onClick={() => setEditingSalesNotes(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4 text-slate-500" /></button>
            </div>
            <div className="p-4 space-y-4">
              <textarea value={salesNotesValue} onChange={(e) => setSalesNotesValue(e.target.value)} className="w-full h-32 p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none" placeholder="Enter sales notes..." autoFocus data-testid="sales-notes-textarea" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditingSalesNotes(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button onClick={handleSalesNotesUpdate} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2" data-testid="save-sales-notes-btn">
                  <Check className="w-4 h-4" /> Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pregnancy Notes Editor Modal */}
      {editingPregnancyNotes && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Edit Pregnancy Notes</h3>
              <button onClick={() => setEditingPregnancyNotes(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4 text-slate-500" /></button>
            </div>
            <div className="p-4 space-y-4">
              <textarea value={pregnancyNotesValue} onChange={(e) => setPregnancyNotesValue(e.target.value)} className="w-full h-32 p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 resize-none" placeholder="Enter pregnancy notes..." autoFocus data-testid="pregnancy-notes-textarea" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditingPregnancyNotes(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button onClick={handlePregnancyNotesUpdate} className="px-4 py-2 text-sm bg-pink-600 text-white rounded-lg hover:bg-pink-700 flex items-center gap-2" data-testid="save-pregnancy-notes-btn">
                  <Check className="w-4 h-4" /> Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Callback DateTime Modal */}
      {editingCallback && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Clock className="w-5 h-5 text-indigo-600" /> Schedule Callback</h3>
              <button onClick={() => setEditingCallback(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4 text-slate-500" /></button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-slate-600">Select the date and time for the callback. You'll receive a notification 60 minutes before the scheduled time.</p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Callback Date & Time</label>
                <input type="datetime-local" value={callbackDateTime} onChange={(e) => setCallbackDateTime(e.target.value)} className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" min={new Date().toISOString().slice(0, 16)} data-testid="callback-datetime-input" />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditingCallback(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button onClick={handleCallbackSubmit} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2" data-testid="save-callback-btn">
                  <Check className="w-4 h-4" /> Schedule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

LeadTable.displayName = 'LeadTable';
export default LeadTable;
